import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle, AlertCircle, Phone, Mail, ArrowLeft, Zap, Package, Calendar } from 'lucide-react';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import SEOHead from '../components/SEO/SEOHead';
import { useBrand } from '../components/BrandProvider';

const ServiceChronosPage = () => {
  const brand = useBrand();

  const conditions = [
    { 
      icon: Calendar, 
      title: "Jour ouvrable", 
      description: "Pas un samedi, dimanche, jour férié ou période de fermeture de nos ateliers" 
    },
    { 
      icon: Clock, 
      title: "Avant 9h00", 
      description: "Commande passée avant 9h le jour J (sinon traitée le jour suivant)" 
    },
    { 
      icon: Package, 
      title: "Collection CHRONOS™", 
      description: "Uniquement les toiles de la collection S&P Sélection CHRONOS™" 
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`Service Chronos™ - Fabrication 24h - ${brand.name}`}
        description="Service express : votre toile de store banne sur mesure fabriquée en 24 heures. Commande prioritaire, accès coupe-file en production."
        url="/service-chronos"
      />

      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo showTagline={true} clickable={true} />
        </div>
      </header>

      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-celadon to-emerald-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-orange-500 p-4 rounded-full">
              <Clock className="w-16 h-16" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Service Chronos™
          </h1>
          <p className="text-xl sm:text-2xl opacity-90 mb-2">
            Votre toile en 24 heures !
          </p>
          <p className="text-lg opacity-75">
            Sans équivalent sur le marché
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Introduction */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="text-celadon" />
            Vous êtes extrêmement pressé ?
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            Votre toile de store a été endommagée par les intempéries. Vous avez une fête de famille programmée 
            prochainement, et vous avez impérativement besoin de votre nouvelle toile pour cette date ?
          </p>
          <p className="text-gray-900 text-xl font-semibold">
            Commandez votre toile de store sur mesure fabriquée en <span className="text-celadon">24 heures</span> seulement !
          </p>
        </div>

        {/* How it works */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Comment ça fonctionne ?</h2>
          
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Votre commande est traitée <strong>en priorité</strong>, et vous bénéficiez d'un accès 
              <strong> « coupe-file »</strong> dans notre processus de production.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              En clair, vous devenez prioritaire, et <strong>passez devant tout le monde</strong> dans le processus de production.
              Elle est fabriquée dans un délai maximal de 24 heures, et expédiée immédiatement.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-celadon text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-semibold mb-2">Commandez</h3>
              <p className="text-gray-600 text-sm">Choisissez une toile CHRONOS™ et passez commande avant 9h</p>
            </div>
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-celadon text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-semibold mb-2">Fabrication express</h3>
              <p className="text-gray-600 text-sm">Votre toile est fabriquée en priorité sous 24h</p>
            </div>
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-celadon text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-semibold mb-2">Expédition à J+1</h3>
              <p className="text-gray-600 text-sm">Expédiée dès le lendemain de votre commande</p>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <AlertCircle className="text-celadon" />
            Conditions du service
          </h2>
          <p className="text-gray-600 mb-6">
            Pour bénéficier de ce traitement préférentiel, votre commande doit remplir les conditions suivantes :
          </p>
          
          <div className="space-y-4">
            {conditions.map((condition, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-lg"
              >
                <div className="bg-green-100 p-2 rounded-full">
                  <condition.icon className="w-5 h-5 text-celadon" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{condition.title}</h4>
                  <p className="text-gray-600 text-sm">{condition.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Eligible Products CTA */}
        <div className="mb-10 bg-gradient-to-r from-celadon to-emerald-600 text-white rounded-xl p-8 text-center">
          <Clock className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Toiles éligibles au Service Chronos™</h2>
          <p className="text-lg opacity-90 mb-6">
            Découvrez notre sélection de toiles disponibles en fabrication express 24h.
          </p>
          <Link 
            to="/?collection=S%26P%20CHRONOS%20%202026%E2%84%A2"
            className="inline-flex items-center gap-2 bg-white text-celadon hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            <Package className="w-5 h-5" />
            Voir la collection S&P CHRONOS™
          </Link>
        </div>

        {/* Important Notice */}
        <div className="mb-10 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="text-amber-600" />
            Important
          </h3>
          <p className="text-gray-700">
            Notre engagement de fabrication en 24 heures ne vaut que sur le <strong>délai de fabrication</strong>. 
            Le délai d'acheminement en revanche ne nous appartient pas, et reste à l'initiative du transporteur.
          </p>
        </div>

        {/* Guarantee */}
        <div className="mb-10 bg-green-50 border border-green-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <CheckCircle className="text-green-600" />
            Notre engagement
          </h3>
          <p className="text-gray-700">
            Si vous passez commande avant 9 heures, votre toile sera expédiée à J+1. 
            <strong> Le délai est garanti.</strong> Nous allons mobiliser notre organisation pour le respecter 
            absolument, et vous servir en priorité.
          </p>
        </div>

        {/* Contact */}
        <div className="bg-gray-900 text-white rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Des questions sur le Service Chronos™ ?</h2>
          <p className="text-gray-300 mb-6">
            Notre équipe est à votre disposition pour vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${brand.contact.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-celadon hover:bg-celadon/90 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <Phone className="w-5 h-5" />
              {brand.contact.phoneDisplay}
            </a>
            <a 
              href={`mailto:${brand.contact.email}?subject=Question Service Chronos™`}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5" />
              Nous contacter
            </a>
          </div>
        </div>

        {/* Back Link */}
        <div className="mt-10 text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-celadon hover:text-celadon/80 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServiceChronosPage;
